import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { motion } from 'framer-motion';
import { Scan, Brain, AlertCircle, CheckCircle, Zap, Shield } from 'lucide-react';

export const CuraScanPage: React.FC = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const menuItems = [
        { label: 'Features', href: '/#features' },
        { label: 'Blog', href: '/blog' },
        { label: 'Press', href: '/press' },
        { label: 'Pitch', href: '/pitch' },
        { label: 'Partnerships', href: '/enterprise-api' }
    ];

    const scrollToSection = (sectionId: string) => {
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const features = [
        {
            icon: <Brain className="w-8 h-8 text-[#003b82]" />,
            title: "AI-Powered Analysis",
            description: "Advanced machine learning algorithms analyze medical imaging with precision, identifying patterns and anomalies that support clinical decision-making."
        },
        {
            icon: <AlertCircle className="w-8 h-8 text-[#003b82]" />,
            title: "Critical Findings Detection",
            description: "Automatically highlights areas requiring immediate attention, ensuring no critical findings are overlooked during initial review."
        },
        {
            icon: <CheckCircle className="w-8 h-8 text-[#003b82]" />,
            title: "Comprehensive Reports",
            description: "Generates detailed summaries with key observations, potential diagnoses, and recommendations for further evaluation."
        },
        {
            icon: <Zap className="w-8 h-8 text-[#003b82]" />,
            title: "Instant Processing",
            description: "Upload and receive preliminary analysis within seconds, accelerating diagnostic workflows without compromising accuracy."
        },
        {
            icon: <Shield className="w-8 h-8 text-[#003b82]" />,
            title: "Clinical Decision Support",
            description: "Works alongside physicians as an intelligent assistant, reducing diagnostic errors and improving patient outcomes."
        },
        {
            icon: <Scan className="w-8 h-8 text-[#003b82]" />,
            title: "Multi-Modality Support",
            description: "Compatible with X-rays, CT scans, MRIs, and other medical imaging formats for comprehensive diagnostic support."
        }
    ];

    return (
        <div className="min-h-screen bg-white">
            <Helmet>
                <title>Cura Scan – AI-Powered Medical Imaging Analysis</title>
                <meta name="description" content="Advanced AI diagnostic support for physicians. Analyze X-rays, CT scans, and MRIs in seconds with clinical-grade accuracy. Coming soon ✓" />
                <link rel="canonical" href="https://mycura.org/scan" />
                <meta property="og:title" content="Cura Scan – AI-Powered Medical Imaging Analysis" />
                <meta property="og:description" content="Advanced AI diagnostic support for physicians. Analyze X-rays, CT scans, and MRIs in seconds with clinical-grade accuracy." />
                <meta property="og:url" content="https://mycura.org/scan" />
                <meta property="og:type" content="website" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Cura Scan – AI-Powered Medical Imaging Analysis" />
                <meta name="twitter:description" content="Advanced AI diagnostic support for physicians. Analyze X-rays, CT scans, and MRIs in seconds with clinical-grade accuracy." />
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "WebPage",
                        "name": "Cura Scan",
                        "url": "https://mycura.org/scan",
                        "isPartOf": { "@type": "WebSite", "name": "Cura", "url": "https://mycura.org" }
                    })}
                </script>
            </Helmet>

            <Navigation
                menuItems={menuItems}
                scrollToSection={scrollToSection}
                scrollToTop={scrollToTop}
                forceWhiteBackground={true}
            />

            {/* Hero Section */}
            <section className="pt-32 pb-20 bg-gradient-to-b from-blue-50 to-white relative overflow-hidden">
                <div className="absolute inset-0 opacity-5">
                    <div className="absolute inset-0" style={{
                        backgroundImage: 'radial-gradient(circle at 2px 2px, #003b82 1px, transparent 0)',
                        backgroundSize: '40px 40px'
                    }}></div>
                </div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-center max-w-4xl mx-auto"
                    >
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 rounded-full mb-6">
                            <Scan className="w-4 h-4 text-[#003b82]" />
                            <span className="text-sm font-medium text-[#003b82]">AI-Powered Diagnostic Support</span>
                        </div>

                        <h1 className="text-5xl md:text-7xl font-['Instrument_Serif'] text-slate-900 mb-6 leading-tight">
                            Cura <span className="italic">Scan</span>
                        </h1>

                        <p className="text-xl md:text-2xl text-slate-900 mb-8 leading-relaxed">
                            Intelligent medical imaging analysis that works alongside physicians to enhance diagnostic accuracy and patient care.
                        </p>

                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <div className="inline-flex items-center justify-center px-8 py-4 bg-slate-100 text-slate-900 rounded-full font-medium cursor-not-allowed">
                                Coming Soon
                            </div>
                            <a
                                href="/enterprise-api"
                                className="inline-flex items-center justify-center px-8 py-4 border-2 border-slate-300 text-slate-700 rounded-full font-medium hover:border-slate-400 transition-colors"
                            >
                                Enterprise Solutions
                            </a>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* How It Works */}
            <section className="py-24 bg-white border-t border-slate-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center max-w-3xl mx-auto mb-16">
                        <h2 className="text-4xl md:text-5xl font-['Instrument_Serif'] text-slate-900 mb-6">
                            How Cura Scan <span className="italic">Works</span>
                        </h2>
                        <p className="text-xl text-slate-900">
                            A seamless workflow designed to integrate into existing clinical practices
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {[
                            {
                                step: "01",
                                title: "Upload Imaging",
                                description: "Securely upload X-rays, CT scans, MRIs, or other medical imaging directly through our HIPAA-compliant platform."
                            },
                            {
                                step: "02",
                                title: "AI Analysis",
                                description: "Our advanced AI processes the imaging in seconds, identifying key findings, anomalies, and areas requiring attention."
                            },
                            {
                                step: "03",
                                title: "Review & Act",
                                description: "Receive a comprehensive summary with highlighted findings, differential diagnoses, and recommendations for next steps."
                            }
                        ].map((item, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                                className="relative"
                            >
                                <div className="text-6xl font-['Instrument_Serif'] text-blue-100 mb-4">{item.step}</div>
                                <h3 className="text-2xl font-['Instrument_Serif'] text-slate-900 mb-3">{item.title}</h3>
                                <p className="text-slate-900 leading-relaxed">{item.description}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Features Grid */}
            <section className="py-24 bg-gray-50 border-t border-slate-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center max-w-3xl mx-auto mb-16">
                        <h2 className="text-4xl md:text-5xl font-['Instrument_Serif'] text-slate-900 mb-6">
                            Designed for <span className="italic">Clinical Excellence</span>
                        </h2>
                        <p className="text-xl text-slate-900">
                            Powerful features that enhance diagnostic workflows and improve patient outcomes
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {features.map((feature, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.05 }}
                                className="bg-white p-8 rounded-2xl border border-slate-200 hover:shadow-lg transition-shadow"
                            >
                                <div className="mb-4">{feature.icon}</div>
                                <h3 className="text-xl font-['Instrument_Serif'] text-slate-900 mb-3">{feature.title}</h3>
                                <p className="text-slate-900 leading-relaxed">{feature.description}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-24 bg-white border-t border-slate-200">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <h2 className="text-4xl md:text-5xl font-['Instrument_Serif'] text-slate-900 mb-6">
                            Ready to enhance your <span className="italic">diagnostic workflow?</span>
                        </h2>
                        <p className="text-xl text-slate-900 mb-8">
                            Join leading healthcare providers using Cura Scan to improve patient care
                        </p>
                        <div className="inline-flex items-center justify-center px-10 py-5 bg-slate-100 text-slate-900 rounded-full font-medium text-lg cursor-not-allowed">
                            Coming Soon
                        </div>
                    </motion.div>
                </div>
            </section>

            <Footer />
        </div>
    );
};
